import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { PrsButtonLg, PrsOutlineButtonLg, PrsButtonStackLg } from '../../../styles-and-themes/styles/prsButtons';
import {
  UamHeading,
  UamBodyText,
  UamFlexColumn,
  UamFormSection,
  UamPageIcon,
} from '../../../styles-and-themes/styles/uam-registration-styles';
import UamBanner from '../../../components/PrsAlert/UamBanner';
import {
  PrsInput,
  PrsFormControl,
  PrsLabel,
  PrsLabelText,
} from '../../../styles-and-themes/styles/prsInputs';
import StepTransitionOverlay from '../../../components/StepTransitionOverlay/StepTransitionOverlay';
import { frontEndUrl } from '../../../destructured-front-end-config';

const LOCK_PERSON_ICON_URL = `${frontEndUrl}static/images/lock_person.svg`;

const QuestionLabel = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #0e0d16;
  margin: 0;
`;

const QuestionText = styled.p`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #0e0d16;
  margin: 0;
  width: 100%;
  min-height: 48px; /* Reserve space for two-line questions */
  overflow-wrap: anywhere;
`;

const SecurityQuestionPanel = ({
  questionText, onSubmit, isSubmitting, errorMessage, onBack, backLabel,
}) => {
  const [answer, setAnswer] = useState('');

  const focusHeadingOnMount = useCallback((node) => {
    if (!node) return;
    if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
    node.focus({ preventScroll: false });
  }, []);

  const handleSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!answer.length || isSubmitting) return;
    const submitted = answer;
    setAnswer('');
    await onSubmit(submitted);
  };

  return (
    <StepTransitionOverlay loading={isSubmitting}>
      <UamFlexColumn gap="0">
        {errorMessage && (
          <UamBanner variant="danger">{errorMessage}</UamBanner>
        )}

        <UamFlexColumn gap="16px" padding="24px 0 0">
          <UamPageIcon src={LOCK_PERSON_ICON_URL} alt="" />

          <UamHeading ref={focusHeadingOnMount}>Verify Your Identity</UamHeading>

          <form onSubmit={handleSubmit}>
            <UamFormSection>
              <UamBodyText>
                To help confirm it&apos;s you, please answer the security question associated with your account.
              </UamBodyText>

              <UamFlexColumn gap="4px" style={{ width: '100%' }}>
                <QuestionLabel>Security Question</QuestionLabel>
                <QuestionText id="fp-security-question"><span>{questionText}</span></QuestionText>
              </UamFlexColumn>

              <PrsFormControl $error={!!errorMessage}>
                <PrsLabel>
                  <PrsLabelText>Answer</PrsLabelText>
                </PrsLabel>
                <PrsInput
                  id="security-answer"
                  type="text"
                  placeholder="Answer your security question"
                  value={answer}
                  autoComplete="off"
                  onChange={(ev) => setAnswer(ev.target.value)}
                  $error={!!errorMessage}
                />
              </PrsFormControl>
            </UamFormSection>

            <PrsButtonStackLg>
              <PrsButtonLg
                type="submit"
                style={{ width: '100%' }}
                disabled={!answer.length || isSubmitting}
              >
                Submit Answer
              </PrsButtonLg>

              {onBack && (
                <PrsOutlineButtonLg
                  type="button"
                  style={{ width: '100%' }}
                  onClick={onBack}
                >
                  {backLabel}
                </PrsOutlineButtonLg>
              )}
            </PrsButtonStackLg>
          </form>
        </UamFlexColumn>
      </UamFlexColumn>
    </StepTransitionOverlay>
  );
};

SecurityQuestionPanel.defaultProps = {
  isSubmitting: false,
  errorMessage: null,
  onBack: null,
  backLabel: 'Back to Sign In',
};

SecurityQuestionPanel.propTypes = {
  questionText: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  errorMessage: PropTypes.string,
  onBack: PropTypes.func,
  backLabel: PropTypes.string,
};

export default SecurityQuestionPanel;
